import { AvatarStyle, BaseStyle, ListItemStyle } from '@cometchat/uikit-elements';
import { CometChatGroupEvents, CometChatMessageEvents, CometChatUIKitConstants, IGroupLeft, IGroupMemberAdded, IGroupMemberJoined, IGroupMemberKickedBanned, IOwnershipChanged, localize } from '@cometchat/uikit-resources';
import { CometChatListItemStyle, MessageHeaderBackButtonStyle, MessageHeaderDivStyle, MessageHeaderListItemStyle, MessageHeaderMenuStyle, defaultAvatarStyle, defaultListItemStyle, getBackButtonStyle, getMessageHeaderWrapperStyle, subtitleStyle, statusIndicatorStyle } from "./style";
import { JSX, useCallback, useContext, useRef, useState } from "react";
import { useCometChatErrorHandler, useRefSync, useStateRef } from "../CometChatCustomHooks";

import BackIcon from './assets/backbutton.svg';
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { CometChatThemeContext } from "../CometChatThemeContext";
import { Hooks } from "./hooks";
import { MessageHeaderStyle } from '@cometchat/uikit-shared';
import PasswordGroupIcon from './assets/Locked.svg';
import PrivateGroupIcon from './assets/Private.svg';
import { Subscription } from 'rxjs';
import { MessageUtils } from '../Shared/Utils/MessageUtils';

interface IMessageHeaderProps {
    avatarStyle?: AvatarStyle,
    statusIndicatorStyle?: BaseStyle,
    messageHeaderStyle?: MessageHeaderStyle,
    listItemStyle?: ListItemStyle,
    subtitleView?: any,
    disableUsersPresence?: boolean,
    disableTyping?: boolean,
    /**
  * @deprecated
  *
  * This property is deprecated as of version 4.3.8 due to newer property 'passwordGroupIcon'. It will be removed in subsequent versions.
  */
    protectedGroupIcon?: string,
    passwordGroupIcon?: string | undefined;
    privateGroupIcon?: string,
    menu?: any,
    user?: CometChat.User,
    group?: CometChat.Group,
    backButtonIconURL?: string
    hideBackButton?: boolean,
    listItemView?: any,
    onError?: ((error: CometChat.CometChatException) => void) | null,
    onBack?: () => void
}

export const CometChatMessageHeader = (props: IMessageHeaderProps) => {
    const { theme } = useContext(CometChatThemeContext);
    const {
        avatarStyle = {},
        statusIndicatorStyle: statusIndicatorStyleObject = null,
        messageHeaderStyle = {},
        listItemStyle = {},
        subtitleView = null,
        disableUsersPresence = false,
        disableTyping = false,
        protectedGroupIcon = PasswordGroupIcon,
        passwordGroupIcon = undefined,
        privateGroupIcon = PrivateGroupIcon,
        menu = null,
        user,
        group,
        backButtonIconURL = BackIcon,
        hideBackButton,
        listItemView = null,
        onError,
        onBack = () => { }
    } = props;

    const [subtitleText, setSubtitleText] = useState('');
    const [loggedInUser, setLoggedInUser] = useState<CometChat.User | null>(null);
    const userRef = useRefSync(user);
    const groupRef = useRefSync(group);
    const onBackRef = useRefSync(onBack);
    const [ccBackBtnElement, setCCBackBtnRef] = useStateRef<JSX.IntrinsicElements["cometchat-button"] | null>(null);
    const isTypingRef = useRef(false);
    const onErrorCallback = useCometChatErrorHandler(onError);


    // const setSubTitle = useCallback(() => {
    //     const user = userRef.current;
    //     const group = groupRef.current;
    //     if (user) {
    //         setSubtitleText(user.getStatus());
    //     }
    //     else if (group) {
    //         const count = group.getMembersCount();
    //         const membersText = localize(count > 1 ? "MEMBERS" : "MEMBER");
    //         setSubtitleText(`${count} ${membersText}`);
    //     }
    // }, [groupRef, userRef]);

    const updateSubtitle = useCallback(() => {
        const user = userRef.current;
        const group = groupRef.current;
        if (user) {
            setSubtitleText(localize(user.getStatus().toUpperCase()));
        }
        else if (group) {
            const count = group.getMembersCount();
            const membersText = localize(count > 1 ? "MEMBERS" : "MEMBER");
            setSubtitleText(`${count} ${membersText}`);
        }
    }, [userRef, groupRef]);

    const updateUserStatus = useCallback((userObject: CometChat.User) => {
        const user = userRef.current;
        if (user) {
            user.setStatus(userObject.getStatus());
            setSubtitleText(user.getStatus());
        }
    }, [userRef]);

    const setTypingIndicatorText = useCallback((typing: CometChat.TypingIndicator) => {
        try {
            const sender = typing?.getSender();
            const receiverId = typing?.getReceiverId();
            if (sender?.getUid() === userRef?.current?.getUid() && loggedInUser?.getUid() === receiverId) {
                let isBlocked = new MessageUtils().getUserStatusVisible(userRef?.current);
                if (isBlocked) {
                    return;
                }
                setSubtitleText(localize("IS_TYPING"));
            }
            if (groupRef?.current?.getGuid() === receiverId) {
                setSubtitleText(`${sender?.getName()} ${localize("IS_TYPING")}`);
            }
        } catch (error: any) {
            onErrorCallback(error);
        }
    }, [userRef, groupRef, onErrorCallback, loggedInUser]);

    const subscribeToEvents = useCallback(() => {
        try {
            const ccGroupMemberAdded = CometChatGroupEvents.ccGroupMemberAdded.subscribe(
                (item: IGroupMemberAdded) => {
                    if (groupRef?.current?.getGuid() === item?.userAddedIn?.getGuid()) {
                        if (item?.usersAdded.length > 0) {
                            item?.usersAdded.forEach(
                                (userAdded: CometChat.User) => {
                                    if (userAdded?.getUid() === loggedInUser?.getUid()) {
                                        groupRef?.current?.setHasJoined(true);
                                    }
                                }
                            )
                        }
                        groupRef?.current?.setMembersCount(item?.userAddedIn?.getMembersCount());
                        updateSubtitle();
                    }
                }
            )
            const ccGroupMemberBanned = CometChatGroupEvents.ccGroupMemberBanned.subscribe(
                (item: IGroupMemberKickedBanned) => {
                    if (groupRef?.current?.getGuid() === item?.kickedFrom?.getGuid()) {
                        if (loggedInUser?.getUid() === item?.kickedUser?.getUid()) {
                            groupRef?.current?.setHasJoined(false);
                        }
                        groupRef?.current?.setMembersCount(item?.kickedFrom?.getMembersCount());
                        updateSubtitle();
                    }
                }
            )
            const ccGroupMemberJoined = CometChatGroupEvents.ccGroupMemberJoined.subscribe(
                (item: IGroupMemberJoined) => {
                    if (groupRef?.current?.getGuid() === item?.joinedGroup?.getGuid()) {
                        if (loggedInUser?.getUid() === item?.joinedUser?.getUid()) {
                            groupRef?.current?.setHasJoined(true);
                        }
                        groupRef?.current?.setMembersCount(item?.joinedGroup?.getMembersCount());
                        updateSubtitle();
                    }
                }
            )
            const ccGroupMemberKicked = CometChatGroupEvents.ccGroupMemberKicked.subscribe(
                (item: IGroupMemberKickedBanned) => {
                    if (groupRef?.current?.getGuid() === item?.kickedFrom?.getGuid()) {
                        if (loggedInUser?.getUid() === item?.kickedUser?.getUid()) {
                            groupRef?.current?.setHasJoined(false);
                        }
                        groupRef?.current?.setMembersCount(item?.kickedFrom?.getMembersCount());
                        updateSubtitle();
                    }
                }
            )
            const ccOwnershipChanged = CometChatGroupEvents.ccOwnershipChanged.subscribe(
                (item: IOwnershipChanged) => {
                    if (groupRef?.current?.getGuid() === item?.group?.getGuid()) {
                        groupRef?.current?.setOwner(item?.group?.getOwner());
                        updateSubtitle();
                    }
                }
            )
            const ccGroupLeft = CometChatGroupEvents.ccGroupLeft.subscribe(
                (item: IGroupLeft) => {
                    if (groupRef?.current?.getGuid() === item?.leftGroup?.getGuid()) {
                        if (loggedInUser?.getUid() === item?.userLeft?.getUid()) {
                            groupRef?.current?.setHasJoined(false);
                        }
                        groupRef?.current?.setMembersCount(item?.leftGroup?.getMembersCount());
                        updateSubtitle();
                    }
                }
            )

            return () => {
                try {
                    ccGroupMemberAdded.unsubscribe();
                    ccGroupMemberBanned.unsubscribe();
                    ccGroupMemberJoined.unsubscribe();
                    ccGroupMemberKicked.unsubscribe();
                    ccOwnershipChanged.unsubscribe();
                    ccGroupLeft.unsubscribe();
                } catch (error: any) {
                    onErrorCallback(error);
                }
            }
        } catch (error: any) {
            onErrorCallback(error);
        }
    }, [groupRef, updateSubtitle, onErrorCallback, loggedInUser]);

    const attachListeners = useCallback(() => {
        const userListenerId = "userList_" + Date.now();
        const groupsListenerId = "groupsList_" + Date.now();
        const connectionListenerId = "connection_" + Date.now();

        if (!disableUsersPresence) {
            CometChat.addUserListener(
                userListenerId,
                new CometChat.UserListener({
                    onUserOnline: (onlineUser: CometChat.User) => {
                        if (userRef.current?.getUid() === onlineUser.getUid()) {
                            updateUserStatus(onlineUser);
                        }
                    },
                    onUserOffline: (offlineUser: CometChat.User) => {
                        if (userRef.current?.getUid() === offlineUser?.getUid()) {
                            updateUserStatus(offlineUser);
                        }
                    },
                })
            );
        }
        let onTypingStarted: Subscription;
        let onTypingEnded: Subscription;
        if (!disableTyping) {
            onTypingStarted = CometChatMessageEvents.onTypingStarted.subscribe((typingIndicator: CometChat.TypingIndicator) => {
                isTypingRef.current = true;
                setTypingIndicatorText(typingIndicator);
            }) as any;
            onTypingEnded = CometChatMessageEvents.onTypingEnded.subscribe((typingIndicator: CometChat.TypingIndicator) => {
                isTypingRef.current = false;
                updateSubtitle();
            }) as any;
        }
        CometChat.addGroupListener(
            groupsListenerId,
            new CometChat.GroupListener({
                onGroupMemberScopeChanged: (message: CometChat.Action, changedUser: CometChat.User, newScope: CometChat.GroupMemberScope, oldScope: CometChat.GroupMemberScope, changedGroup: CometChat.Group) => {
                    if (groupRef.current?.getGuid() === changedGroup?.getGuid() && changedUser.getUid() === loggedInUser?.getUid()) {
                        groupRef.current?.setScope(newScope);
                    }
                    updateSubtitle();
                },
                onGroupMemberKicked: (message: CometChat.Action, kickedUser: CometChat.User, kickedBy: CometChat.User, kickedFrom: CometChat.Group) => {
                    if (groupRef.current?.getGuid() === kickedFrom?.getGuid()) {
                        if (kickedUser.getUid() === loggedInUser?.getUid()) {
                            groupRef.current?.setHasJoined(false);
                        }
                        groupRef.current?.setMembersCount(kickedFrom?.getMembersCount());
                        updateSubtitle();
                    }
                },
                onMemberAddedToGroup: (message: CometChat.Action, userAdded: CometChat.User, userAddedBy: CometChat.User, userAddedIn: CometChat.Group) => {
                    if (groupRef.current?.getGuid() === userAddedIn.getGuid()) {
                        if (userAdded.getUid() === loggedInUser?.getUid()) {
                            groupRef.current?.setHasJoined(true);
                        }
                        groupRef.current?.setMembersCount(userAddedIn?.getMembersCount());
                        updateSubtitle();
                    }
                },
                onGroupMemberLeft: (message: CometChat.Action, leavingUser: CometChat.User, groupObject: CometChat.Group) => {
                    if (groupRef.current?.getGuid() === groupObject.getGuid()) {
                        if (leavingUser.getUid() === loggedInUser?.getUid()) {
                            groupRef.current?.setHasJoined(false);
                        }
                        groupRef.current?.setMembersCount(groupObject.getMembersCount());
                        updateSubtitle();
                    }
                },
                onGroupMemberJoined: (message: CometChat.Action, joinedUser: CometChat.User, joinedGroup: CometChat.Group) => {
                    if (groupRef.current?.getGuid() === joinedGroup.getGuid()) {
                        if (joinedUser.getUid() === loggedInUser?.getUid()) {
                            groupRef.current?.setHasJoined(true);
                        }
                        groupRef.current?.setMembersCount(joinedGroup.getMembersCount());
                        updateSubtitle();
                    }
                },
                onGroupMemberBanned: (message: CometChat.Action, bannedUser: CometChat.User, bannedBy: CometChat.User, bannedFrom: CometChat.Group) => {
                    if (groupRef.current?.getGuid() === bannedFrom.getGuid()) {
                        if (bannedUser.getUid() === loggedInUser?.getUid()) {
                            groupRef.current?.setHasJoined(false);
                        }
                        groupRef.current?.setMembersCount(bannedFrom.getMembersCount());
                        updateSubtitle();
                    }
                }
            })
        );
        CometChat.addConnectionListener(
            connectionListenerId,
            new CometChat.ConnectionListener({
                onConnected: () => {
                    console.log("ConnectionListener => On Connected");
                },

            })
        );
        return () => {
            CometChat.removeUserListener(userListenerId);
            CometChat.removeGroupListener(groupsListenerId);
            CometChat.removeConnectionListener(connectionListenerId);
            onTypingStarted?.unsubscribe();
            onTypingEnded?.unsubscribe();
        };
    }, [userRef, groupRef, updateUserStatus, disableTyping, updateSubtitle, setTypingIndicatorText, loggedInUser, disableUsersPresence]);

    const checkStatusType = useCallback(() => {
        let userBlockedFlag = new MessageUtils().getUserStatusVisible(userRef.current);
        if (userRef.current) {
            if (!disableUsersPresence && !userBlockedFlag) {
                return "#00f300";
            }
        }
        else if (groupRef.current) {
            const groupType = groupRef.current.getType();
            if (groupType === CometChatUIKitConstants.GroupTypes.private) {
                return "#00f300";
            }
            else if (groupType === CometChatUIKitConstants.GroupTypes.password) {
                return "#F7A500";
            }
        }
        return null;
    }, [userRef, groupRef, disableUsersPresence]);

    const checkGroupType = useCallback(() => {
        let image: string = "";
        if (groupRef.current) {
            switch (groupRef.current?.getType()) {
                case CometChatUIKitConstants.GroupTypes.password:
                    image = passwordGroupIcon || protectedGroupIcon;
                    break;
                case CometChatUIKitConstants.GroupTypes.private:
                    image = privateGroupIcon;
                    break;
                default:
                    image = ""
                    break;
            }
        }
        return image
    }, [groupRef, protectedGroupIcon, privateGroupIcon, passwordGroupIcon]);

    const getAvatarStyle = useCallback(() => {
        return { ...new AvatarStyle(defaultAvatarStyle(theme)), ...avatarStyle };
    }, [avatarStyle, theme]);

    const getStatusIndicatorStyle = (userOrGroup: CometChat.User | CometChat.Group | undefined): BaseStyle | null => {      
        if (userOrGroup instanceof CometChat.User) {
          let userBlockedFlag = new MessageUtils().getUserStatusVisible(userOrGroup);
          
          if (!disableUsersPresence && !userBlockedFlag) {            
            return (
                statusIndicatorStyle(
                statusIndicatorStyleObject
                )
            );
          }
          return null;
        } else {
          return null;
        }
      };
      

    const getListItemStyle = useCallback(() => {
        return defaultListItemStyle(listItemStyle, theme);
    }, [listItemStyle, theme]);

    const getSubtitleView = useCallback(() => {
        if (subtitleView) {
            return subtitleView;
        }
        const userBlockedFlag = userRef.current?.getBlockedByMe() || userRef.current?.getHasBlockedMe();
        return !userBlockedFlag ? (
            <div>
                <cometchat-label
                    text={subtitleText}
                    labelStyle={JSON.stringify(subtitleStyle(userRef.current, isTypingRef, messageHeaderStyle, theme))}
                />
            </div>
        ) : null;
    }, [userRef, subtitleView, subtitleText, theme, messageHeaderStyle]);

    const getListItemView = useCallback(() => {
        if (listItemView) {
            return listItemView;
        } else {
            let userOrGroup = userRef.current || groupRef.current
            return (
                <cometchat-list-item avatarName={userRef.current?.getName() || groupRef.current?.getName()}
                    avatarURL={userRef.current?.getAvatar() || groupRef.current?.getIcon()} listItemStyle={JSON.stringify(getListItemStyle())}
                    statusIndicatorColor={checkStatusType()} statusIndicatorIcon={checkGroupType()}
                    title={userRef.current?.getName() || groupRef.current?.getName()} hideSeparator={true}
                    statusIndicatorStyle={JSON.stringify(getStatusIndicatorStyle(userOrGroup))} avatarStyle={JSON.stringify(getAvatarStyle())} style={CometChatListItemStyle()}>
                    <div slot="subtitleView">
                        {getSubtitleView()}
                    </div>
                </cometchat-list-item>
            )
        }
    }, [userRef, groupRef, listItemView, getSubtitleView, getAvatarStyle, getStatusIndicatorStyle, getListItemStyle, checkGroupType, checkStatusType]);

    const getBackButton = () => {

        if (hideBackButton) return null;

        return (
            <div className="cc-message-header__back-button" style={MessageHeaderBackButtonStyle()}>
                <cometchat-button iconURL={backButtonIconURL} buttonStyle={JSON.stringify(getBackButtonStyle(theme))} ref={setCCBackBtnRef}></cometchat-button>
            </div>
        );
    };

    const getMenu = useCallback(() => {

        if (!menu) return null;

        return (
            <div className="cc-message-header__menu" style={MessageHeaderMenuStyle()}>
                {menu}
            </div>
        );

    }, [menu]);

    Hooks(
        loggedInUser,
        setLoggedInUser,
        subscribeToEvents,
        attachListeners,
        onErrorCallback,
        ccBackBtnElement,
        onBackRef,
        userRef,
        groupRef,
        updateSubtitle
    )

    return (
        <div className="cc-message-header__wrapper" style={getMessageHeaderWrapperStyle(messageHeaderStyle, theme)}>
            <div className="cc-message-header" style={MessageHeaderDivStyle()}>
                {getBackButton()}
                <div className="cc-message-header__listitem" style={MessageHeaderListItemStyle()}>
                    {getListItemView()}
                </div>
            </div>
            {getMenu()}
        </div>
    );
}
